/**
 * R-3 (DA-BUG-1134) — "Send test to device". Creates a short-lived preview token for the saved
 * draft (RRS POST /in-app/preview-token {messageId, testContactId?} → {token, expiresAt, deepLink};
 * 10 min, 5 opens, invalid once the message is edited) and shows it as a QR code plus a copyable
 * deep link. The phone's app hands the link to the SDK (handlePreviewLink), which shows the draft
 * at once with no trigger, audience, caps or analytics. With a test contact, the server fills its
 * non-identifying attributes; email, phone and ids show as their fallback or tag.
 */
import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, Input, Text, FormControl, FormLabel, FormHelperText, Box, Flex, Center,
} from '@chakra-ui/react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { getApiHeaders } from '../../../utils/DefaultFunction';

const msgOf = (e) => e?.response?.data?.message || e?.message || 'Could not create the test link.';

export default function SendTestModal({ isOpen, onClose, messageId }) {
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [link, setLink] = useState(null); // { token, expiresAt, deepLink }
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (isOpen) { setErr(null); setLink(null); setCopied(false); } }, [isOpen]);

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const body = { messageId, ...(contact.trim() ? { testContactId: contact.trim() } : {}) };
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/in-app/preview-token`, body, { headers: getApiHeaders() });
      if (!data?.data?.token) throw new Error('The server did not return a link.');
      setLink(data.data);
    } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); }
  };

  // R-3 QR (DA-BUG-1421): the QR + Copy carry the WEB link the phone scanner can "Open" (http(s)),
  // served by RRS GET /p/:token, which hands the device the xnotify-preview:// deep link. A raw custom
  // scheme is not offered as "Open" by scanners.
  const webLink = link?.token ? `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/p/${link.token}` : '';

  const copy = async () => {
    try { await navigator.clipboard.writeText(webLink); setCopied(true); } catch { setCopied(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="14px">
        <ModalHeader fontSize="16px">Send test to device</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={3}>
            Scan the code with the phone (or open the link on it). The app shows this draft at once — no trigger, audience, caps or analytics.
          </Text>
          <FormControl mb={3}>
            <FormLabel fontSize="sm" mb={1}>Test contact id (optional)</FormLabel>
            <Input size="sm" aria-label="Test contact id (optional)" placeholder="Contact id" value={contact} onChange={(e) => setContact(e.target.value)} />
            <FormHelperText fontSize="xs">Fills that contact's name, tier and custom attributes. Email, phone and ids are never filled; they show their fallback.</FormHelperText>
          </FormControl>
          {err && <Text fontSize="sm" color="red.500" mb={2}>{err}</Text>}
          {link && (
            <Box borderWidth="1px" borderRadius="10px" p={3}>
              <Center mb={3} data-testid="preview-qr"><QRCodeSVG value={webLink} size={180} includeMargin /></Center>
              <Flex gap={2}>
                <Input size="sm" fontFamily="mono" readOnly aria-label="Test preview link" value={webLink} />
                <Button size="sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Expires {link.expiresAt ? new Date(link.expiresAt).toLocaleTimeString() : 'in 10 minutes'} · up to 5 opens · editing the message invalidates it.
              </Text>
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          <Button size="sm" colorScheme="blue" onClick={create} isLoading={busy} isDisabled={!messageId}>{link ? 'Create a new link' : 'Create test link'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
